/**
 * Number formatting utilities for consistent display across the application
 */

/**
 * Format a number with commas as thousands separators
 * @param value - The number to format
 * @returns Formatted string with commas
 */
export const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0'
  }
  return value.toLocaleString('en-US')
}

/**
 * Format a currency value with dollar sign and commas
 * @param value - The value in dollars
 * @param showCents - Whether to show cents (default: false for large numbers)
 * @returns Formatted currency string
 */
export const formatCurrency = (value: number | null | undefined, showCents: boolean = false): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0'
  }
  
  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  }
  
  return value.toLocaleString('en-US', options)
}

/**
 * Format large numbers with appropriate suffixes (K, M, B, T)
 * @param value - The number to format
 * @param precision - Number of decimal places (default: 1)
 * @returns Formatted string with suffix
 */
export const formatLargeNumber = (value: number | null | undefined, precision: number = 1): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0'
  }
  
  const absValue = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  
  if (absValue >= 1e12) {
    return `${sign}${(absValue / 1e12).toFixed(precision)}T`
  } else if (absValue >= 1e9) {
    return `${sign}${(absValue / 1e9).toFixed(precision)}B`
  } else if (absValue >= 1e6) {
    return `${sign}${(absValue / 1e6).toFixed(precision)}M`
  } else if (absValue >= 1e3) {
    return `${sign}${(absValue / 1e3).toFixed(precision)}K`
  } else {
    return `${sign}${absValue.toFixed(0)}`
  }
}

/**
 * Format currency values in millions with M suffix
 * @param value - The value in millions
 * @param precision - Number of decimal places (default: 0)
 * @returns Formatted currency string with M suffix
 */
export const formatCurrencyMillion = (value: number | null | undefined, precision: number = 0): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0M'
  }
  
  if (value === 0) {
    return '$0M'
  }
  
  return `$${value.toFixed(precision)}M`
}

/**
 * Format shares in millions
 * @param value - The number of shares in millions
 * @param precision - Number of decimal places (default: 0)
 * @returns Formatted string with M suffix
 */
export const formatSharesMillion = (value: number | null | undefined, precision: number = 0): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0M'
  }
  
  if (value === 0) {
    return '0M'
  }
  
  return `${value.toFixed(precision)}M`
}

/**
 * Format a percentage value
 * @param value - The percentage value (0-100)
 * @param precision - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number | null | undefined, precision: number = 1): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%'
  }
  
  return `${value.toFixed(precision)}%`
}

/**
 * Format placeholder text for number inputs with proper formatting
 * @param value - The placeholder value
 * @param type - The type of formatting to apply
 * @returns Formatted placeholder string
 */
export const formatPlaceholder = (value: number, type: 'currency' | 'number' | 'million'): string => {
  switch (type) {
    case 'currency':
      return formatCurrency(value)
    case 'million':
      return formatLargeNumber(value)
    case 'number':
    default:
      return formatNumber(value)
  }
} 